/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * useSelfCheckInWrite — one self check-in or check-out, for one event.
 *
 * THE SAME WRITE PATH THE REGISTER BUTTON USES, deliberately: a stored action
 * button on the event view, submitted by id, with the server deciding every
 * column from its own configuration. The browser sends the confirmed details
 * and the pass id — the only way to say which seat — and the server proves the
 * pass belongs to the sender (the button's owner_checked rule) before it
 * writes anything.
 *
 * `busy` and `error` are this flow's own. `run` resolves true only for a write
 * the server confirmed, after `reload` has re-read the passes, so what the
 * screen says next came from the database rather than from this browser's
 * hope about what the write did.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import usePublicAction from './usePublicAction';

/** The event view the check-in action buttons are stored on. */
const EVENT_SLUG = 'event';

const NO_BUTTONS = [];

export default function useSelfCheckInWrite({ eventId, reload }) {
  const { runAction } = usePublicAction({
    tableSlug: EVENT_SLUG, recordId: eventId, actionButtons: NO_BUTTONS,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Set false on unmount, so an answer that lands after the visitor has left
  // is dropped — and TRUE again on mount, because StrictMode mounts twice.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const run = useCallback(async (buttonId, { userInfo, passId }) => {
    setBusy(true);
    setError(null);
    const result = await runAction({
      button: { id: buttonId },
      // Nothing in `answers`: those belong to a Form Builder form, and this
      // button asks its questions itself. The values travel as payload,
      // confined by the button's allowlist like any other.
      answers: {},
      payload: {
        ...(passId ? { pass_id: passId } : {}),
        // The pass's user_info as it should END UP — already merged, because
        // the write path REPLACES a json column rather than merging into it.
        ...(userInfo || {}),
      },
    });

    if (!aliveRef.current) return false;

    if (result?.requiresAuth) {
      window.location.href = `/sign-in?next=${encodeURIComponent(window.location.pathname)}`;
      return false;
    }
    if (!result?.ok) {
      // The server's own sentence where there is one — "Check-in has closed",
      // "already checked in" — because that is the only text that tells
      // somebody what actually happened.
      setError(result?.message || 'That did not save. Nothing was changed — please try again.');
      setBusy(false);
      return false;
    }

    if (reload) await reload();
    if (aliveRef.current) setBusy(false);
    return true;
  }, [runAction, reload]);

  const clearError = useCallback(() => setError(null), []);

  return { busy, error, run, clearError };
}
